import { useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useBreadcrumbs, BreadcrumbItem } from '@/contexts/Breadcrumbs'

function titleCase(s: string) {
  return s.replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, ch => ch.toUpperCase())
}

const accountLabels: Record<string, string> = {
  profile: 'Profile',
  security: 'Security',
  backups: 'Backup Vault',
  activity: 'Activity',
  'api-tokens': 'API tokens',
  'ssh-keys': 'SSH keys',
  'connected-services': 'Connected services',
}

export default function BreadcrumbsAuto() {
  const location = useLocation()
  const params = useParams()
  const { setItems } = useBreadcrumbs()

  // Include params in the dependency to ensure updates when route params change
  const items = useMemo<BreadcrumbItem[]>(() => {
    const path = location.pathname.replace(/\/+$/, '') || '/'
    const segs = path.split('/').filter(Boolean)
    const searchParams = new URLSearchParams(location.search)

    // Always start with Dashboard
    const crumbs: BreadcrumbItem[] = []
    if (path === '/') {
      crumbs.push({ label: 'Dashboard' })
      return crumbs
    }
    crumbs.push({ label: 'Dashboard', to: '/' })

    // Top-level routes
    if (segs[0] === 'servers') {
      crumbs.push({ label: 'Hosting' })
      crumbs.push({ label: 'Servers', to: '/servers' })
      if (segs.length >= 2) {
        const uuid = segs[1]
        const short = uuid?.slice(0, 8)
        const base = `/servers/${uuid}`
        if (segs.length === 2) {
          crumbs.push({ label: short || uuid })
        } else {
          crumbs.push({ label: short || uuid, to: base })
          crumbs.push({ label: titleCase(segs[2]) })
        }
      }
      return crumbs
    }

    if (segs[0] === 'compute') {
      // compute/deploy[/service]
      crumbs.push({ label: 'Hosting' })
      crumbs.push({ label: 'Servers', to: '/servers' })
      crumbs.push({ label: 'Create New Server', to: '/compute/deploy' })
      if (segs[1] === 'deploy' && segs[2] === 'service') {
        const name = searchParams.get('name')
        if (name) crumbs.push({ label: decodeURIComponent(name) })
      }
      return crumbs
    }

    if (segs[0] === 'account') {
      crumbs.push({ label: 'Account', to: '/account' })
      if (segs.length >= 2) crumbs.push({ label: accountLabels[segs[1]] || titleCase(segs[1]) })
      return crumbs
    }

    if (segs[0] === 'billing') {
      crumbs.push({ label: 'Billing' })
      return crumbs
    }

    if (segs[0] === 'game-servers' && segs[1] === 'networks') {
      crumbs.push({ label: 'Hosting' })
      crumbs.push({ label: 'Servers', to: '/servers' })
      if (segs[2]) {
        crumbs.push({ label: 'Minecraft Networks', to: '/game-servers/networks' })
        crumbs.push({ label: segs[2].slice(0, 8) })
      } else {
        crumbs.push({ label: 'Minecraft Networks' })
      }
      return crumbs
    }

    if (segs[0] === 'dns') {
      crumbs.push({ label: 'DNS', to: '/dns/subdomains' })
      if (segs.length >= 2) crumbs.push({ label: titleCase(segs[1]) })
      return crumbs
    }

    if (segs[0] === 'support') {
      crumbs.push({ label: 'Support', to: '/support' })
      if (segs[1] === 'cases') crumbs.push({ label: 'My Cases' })
      else if (segs[1] === 'new') crumbs.push({ label: 'Submit Case' })
      else if (segs[1] === 'tickets') {
        crumbs.push({ label: 'My Cases', to: '/support/cases' })
        if (segs[2]) crumbs.push({ label: segs[2].slice(0, 8) })
      } else if (segs.length === 1) crumbs[crumbs.length - 1] = { label: 'Support' }
      return crumbs
    }

    if (segs[0] === 'admin') {
      crumbs.push({ label: 'Admin', to: '/admin' })
      if (segs.length >= 2) {
        const map: Record<string, string> = {
          dns: 'DNS',
          stripe: 'Stripe',
          moderation: 'Moderation',
        }
        const label = map[segs[1]] || titleCase(segs[1])
        crumbs.push({ label })
      }
      return crumbs
    }

    // Fallback: show all segments
    let acc = ''
    for (let i = 0; i < segs.length; i++) {
      acc += `/${segs[i]}`
      const label = titleCase(segs[i])
      if (i < segs.length - 1) crumbs.push({ label, to: acc })
      else crumbs.push({ label })
    }
    return crumbs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, JSON.stringify(params)])

  useEffect(() => setItems(items), [items, setItems])
  return null
}
